/**
 * Payments and transactions — a **read-only mirror of the gateway**.
 *
 * No charge can be created here and no payment edited. That is the whole safety model of this
 * screen, and the copy says it rather than assuming somebody knows.
 *
 * **How much of a payment you see depends on your scope.** Amounts are visible to everyone who
 * may read this console; the payment instrument is finance's alone (admin-14 §1 footnote 4).
 * Support and dispatch need to know whether a booking is paid and for how much — that is half
 * their job — but which bank a devotee uses is not needed to reschedule a puja. The withholding
 * happens on the server, and this screen renders "withheld" from the flag it is told rather than
 * inferring it from an absent field: absent could equally mean the gateway never said.
 *
 * **`failed_provider` is its own tab.** Each of those rows means a devotee saw an error before
 * Razorpay was even reached — a different problem from a declined card, and a different person's
 * to fix.
 *
 * There is no webhook replay and no ledger tab yet: both need endpoints that do not exist
 * (E15, E16). They are absent rather than disabled, because a control that cannot work is worse
 * than one that is not offered.
 */
import { useState } from 'react';
import { Button, StatusChip } from '../components/primitives';
import { BookingLedgerLines, DataTable, DetailPage } from '../components/data';
import { KV } from '../components/entities';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { FilterBar, Section, StatTile } from '../components/kit';
import { formatInr } from '../lib/money';
import { errorMessage } from '../lib/api/client';
import {
  usePaymentDetail,
  usePayments,
  usePaymentsHealth,
  type ConsolePaymentRow,
} from '../lib/api/hooks/payments';
import { useBookingLedger } from '../lib/api/hooks/ledger';
import { useUrlSearch, useUrlTab } from '../lib/filterCodecs';
import { formatDateTime } from '../lib/datetime';

/**
 * The four scopes the server admits to `GET /v1/admin/bookings/{id}/ledger`.
 *
 * ⛔ **Mirrored from `rbac_matrix.py`, never invented here** — and deliberately NOT a runtime
 * guard. Every scope this screen is shown to (`finance`, `ops_dispatch`, `support`) is already in
 * this set, so a `mayReadLedger &&` around the section would be a branch no input can reach, and a
 * guard nobody can exercise is a claim nobody can check.
 *
 * It is exported instead, and a test asserts the screen's own scope list stays a subset of it. The
 * day somebody widens this screen to a fourth scope, that test fails rather than the drawer
 * quietly showing a 403 where a section should be.
 */
export const LEDGER_SCOPES = ['superadmin', 'finance', 'ops_dispatch', 'support'] as const;

/**
 * The ledger tab of the payment drawer.
 *
 * The gateway's story and the ledger's story are different stories about one payment: the timeline
 * above says what the provider did, this says what it did to our books. Support answers "did she
 * pay?" from the first and finance answers "where did it land?" from the second, and they are
 * side by side because the question that starts as the first often becomes the second.
 */
function PaymentLedger({ bookingId }: { bookingId: number }) {
  const trail = useBookingLedger(bookingId);
  if (trail.isPending) return <LoadingState message="Reading the booking's money…" />;
  if (trail.isError) {
    return (
      <ErrorState title="Could not read the booking's money" cause={errorMessage(trail.error)} />
    );
  }
  return <BookingLedgerLines lines={trail.data.lines} title="Where this money landed" />;
}

const TABS = [
  { key: 'captured', label: 'Captured' },
  { key: 'failed_attempts', label: 'In checkout' },
  { key: 'unpaid', label: 'Running out' },
  { key: 'provider_failures', label: 'Gateway failures' },
  { key: 'all', label: 'All' },
];

/** The tab rides the URL (doc 05-admin/04 §3.3), so a pasted link opens the same queue. */
const TAB_KEYS = TABS.map((t) => t.key);

const PAGE_SIZE = 25;

/** What an order's status means to somebody scanning the column. */
export function orderTone(status: string): 'neutral' | 'warn' | 'success' | 'error' {
  if (status === 'paid') return 'success';
  if (status === 'failed_provider') return 'error';
  if (status === 'expired' || status === 'cancelled') return 'warn';
  return 'neutral';
}

/**
 * The instrument column's content.
 *
 * Three states, not two. "Withheld" is a rule about this admin; a dash is the gateway not having
 * told us. Collapsing them would have support believing a UPI payment had no method at all.
 */
export function instrumentLabel(row: ConsolePaymentRow, shown: boolean): string {
  if (!shown) return 'Withheld';
  return row.method_display ?? '—';
}

/** A `failed_provider` row deserves its reason on the row, not two clicks away. */
export function providerFailureNote(row: ConsolePaymentRow): string | null {
  if (row.order_status !== 'failed_provider') return null;
  return 'The devotee saw an error before the gateway was reached.';
}

export function Payments() {
  const [tab, setTab] = useUrlTab('payments', TAB_KEYS, 'captured');
  const [q, setQ] = useUrlSearch('payments');
  const [page, setPage] = useState(1);
  const [openOrderId, setOpenOrderId] = useState<number | null>(null);

  const health = usePaymentsHealth();
  const payments = usePayments({
    tab,
    q: q || undefined,
    page,
    page_size: PAGE_SIZE,
  });
  const detail = usePaymentDetail(openOrderId);

  const rows = payments.data?.items ?? [];
  const shown = payments.data?.shows_instruments ?? false;
  const total = payments.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function narrow(next: () => void) {
    // Any filter change starts at page one. Staying on page 4 of a narrower result is an empty
    // screen that reads as "no matches" when the matches are two pages back.
    setPage(1);
    next();
  }

  if (openOrderId !== null) {
    return (
      <DetailPage
        title={detail.data?.row.booking_public_code ?? 'Payment'}
        code={detail.data?.row.provider_order_id ?? undefined}
        status={
          detail.data ? (
            <StatusChip
              tone={orderTone(detail.data.row.order_status)}
              label={detail.data.row.order_status}
            />
          ) : undefined
        }
        onClose={() => setOpenOrderId(null)}
        audit="A read-only mirror of the gateway. Nothing on this screen can move money."
      >
        {detail.isPending && <LoadingState message="Reading the payment…" />}
        {detail.isError && (
          <ErrorState title="Could not read that payment" cause={errorMessage(detail.error)} />
        )}
        {detail.isSuccess && (
          <>
            <KV
              rows={[
                ['Amount', formatInr(detail.data.row.amount_paise)],
                ['Order status', detail.data.row.order_status],
                ['Payment status', detail.data.row.payment_status ?? 'not paid'],
                ['Payment code', detail.data.row.payment_public_code ?? '—'],
                ['Provider order', detail.data.row.provider_order_id ?? '—'],
                ['Provider payment', detail.data.row.provider_payment_id ?? '—'],
                [
                  'Paid via',
                  instrumentLabel(detail.data.row, detail.data.shows_instruments),
                ],
                [
                  'Captured',
                  detail.data.row.captured_at
                    ? formatDateTime(detail.data.row.captured_at)
                    : '—',
                ],
              ]}
            />

            <Section
              title="What happened"
              note="What the platform did, and what the devotee's device reported while it did"
              pad={false}
            >
              <DataTable
                density="compact"
                columns={[
                  { key: 'at', label: 'When', mono: true },
                  { key: 'kind', label: 'Event' },
                  { key: 'detail', label: 'Detail' },
                  { key: 'provider', label: 'The gateway said' },
                ]}
                rows={detail.data.timeline.map((entry, index) => ({
                  id: `${entry.kind}-${index}`,
                  at: formatDateTime(entry.at),
                  kind: entry.kind,
                  detail: entry.detail,
                  // Verbatim. Paraphrasing an error is how support ends up describing a problem
                  // the provider's own dashboard calls something else.
                  provider: entry.provider_text ?? '—',
                }))}
              />
            </Section>

            <PaymentLedger bookingId={detail.data.row.booking_id} />
          </>
        )}
      </DetailPage>
    );
  }

  return (
    <>
      <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
        A read-only mirror of the gateway. No charge can be created here and no payment edited.
        {!shown && payments.isSuccess
          ? ' Payment instruments are withheld from your role — amounts and status only.'
          : ''}
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
          gap: 'var(--sp-md)',
        }}
      >
        <StatTile
          label="Captured, 24h"
          value={health.data ? formatInr(health.data.captured_paise) : '—'}
          note="money actually taken"
        />
        <StatTile
          label="Failed attempts, 24h"
          value={health.data?.failed_attempts ?? '—'}
          note="declines and abandonments"
        />
        <StatTile
          label="Orders still open"
          value={health.data?.orders_unpaid ?? '—'}
          note="inside their hold"
        />
        <StatTile
          label="Gateway failures, 24h"
          value={health.data?.provider_failures ?? '—'}
          note="a devotee saw an error"
          tone={(health.data?.provider_failures ?? 0) > 0 ? 'error' : 'default'}
        />
      </div>

      <FilterBar
        q={q}
        onQ={(value) => narrow(() => setQ(value))}
        placeholder="BMP code, order or payment id — press /"
        tabs={TABS}
        tab={tab}
        onTab={(next) => narrow(() => setTab(next))}
      />

      {payments.isPending && <LoadingState message="Reading payments…" />}
      {payments.isError && (
        <ErrorState title="Could not read payments" cause={errorMessage(payments.error)} />
      )}
      {payments.isSuccess && rows.length === 0 && (
        <EmptyState
          icon="credit-card"
          title={tab === 'provider_failures' ? 'No gateway failures' : 'No payments here'}
          body={
            tab === 'provider_failures'
              ? 'Every checkout that opened reached the gateway.'
              : 'Nothing matches this filter. Widen it, or try the All tab.'
          }
          inline
        />
      )}

      {rows.length > 0 && (
        <Section
          title="Payments"
          note={`${total} ${total === 1 ? 'order' : 'orders'} · page ${page} of ${pages}`}
          pad={false}
        >
          <DataTable
            density="compact"
            onRowClick={(row) => setOpenOrderId(Number(row.id))}
            columns={[
              { key: 'booking', label: 'Booking', mono: true },
              { key: 'amount', label: 'Amount', align: 'right', mono: true },
              { key: 'order', label: 'Order' },
              { key: 'attempt', label: 'Last attempt' },
              { key: 'instrument', label: 'Paid via' },
              { key: 'captured', label: 'Captured', mono: true },
            ]}
            rows={rows.map((row) => ({
              id: row.order_id,
              booking: row.booking_public_code ?? `#${row.booking_id}`,
              amount: formatInr(row.amount_paise),
              order: (
                <StatusChip
                  tone={row.is_running_out ? 'warn' : orderTone(row.order_status)}
                  label={row.is_running_out ? 'running out' : row.order_status}
                />
              ),
              attempt: row.latest_attempt_status ?? '—',
              instrument: instrumentLabel(row, shown),
              captured: row.captured_at
                ? formatDateTime(row.captured_at)
                : '—',
            }))}
          />
        </Section>
      )}

      {rows.length > 0 && pages > 1 && (
        <div style={{ display: 'flex', gap: 'var(--sp-sm)', justifyContent: 'flex-end' }}>
          <Button variant="secondary" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      )}
    </>
  );
}
